import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Download, RefreshCw, AlertTriangle, Info, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { IRC_S1 } from "@/constants/testIds";

// Round 19 · IRC Form S1 · six real form columns.
//   1 Gross wages (LSL included) · 2 Taxable allowances · 3 Taxable benefits
//   4 Non-taxable allowances (K0) · 5a/5b Termination (K0) · 6 SWT
// Dependants render as an employee-block detail — NOT a column.

const defaultYear = () => String(new Date().getFullYear());

export default function IrcS1() {
    const [year, setYear] = useState(defaultYear());
    const [env, setEnv] = useState(null);
    const [loading, setLoading] = useState(false);
    const years = useMemo(() => {
        const y = new Date().getFullYear();
        return Array.from({ length: 9 }, (_, i) => String(y - i + 2)).reverse();
    }, []);

    const load = async (y = year) => {
        setLoading(true);
        try {
            const { data } = await api.get(`/irc/s1/preview?year=${y}`);
            setEnv(data);
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail) || "Failed to load S1");
            setEnv(null);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(year); /* eslint-disable-next-line */ }, [year]);

    const download = () => {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/irc/s1.pdf?year=${year}`;
        const token = localStorage.getItem("mss_token");
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => { if (!r.ok) throw new Error(r.status); return r.blob(); })
            .then((b) => {
                const a = document.createElement("a");
                a.href = URL.createObjectURL(b);
                a.download = `irc_s1_${year}.pdf`;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(a.href);
            })
            .catch((e) => toast.error(`Download failed (${e.message})`));
    };

    const rows = env?.rows || [];
    const totals = env?.totals || {};
    const missing = env?.missing_header_fields || [];
    const travelCol = env?.travel_column || 2;
    const travelConfirmed = !!env?.travel_column_confirmed;

    return (
        <div data-testid={IRC_S1.page} className="p-10 max-w-[1300px]">
            <div className="mb-8 flex items-start justify-between gap-6">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.24em] text-neutral-500 font-display">
                        Reports · Internal Revenue Commission
                    </div>
                    <h1 className="font-display font-bold text-4xl mt-1 text-[color:var(--kp-charcoal)]">
                        Form S1 · Annual reconciliation
                    </h1>
                    <p className="text-sm text-neutral-500 mt-2 leading-relaxed max-w-[820px]">
                        One row per employee. Col 1 is wages only (LSL included — leave pay is wages).
                        Col 2 is taxable allowances (bus, school, other). Col 3 is taxable benefits.
                        Travel is currently routed to <strong>column {travelCol}</strong> — change
                        in <a className="underline" href="/settings/payroll">Settings</a> (setting
                        <code className="mx-1 font-mono-money">s1_travel_column</code>). Cols 4 and 5
                        are K0 (nothing modelled). Dependants show declared and rebate-used in the
                        employee block.
                    </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <select data-testid={IRC_S1.yearPicker} value={year} onChange={(e) => setYear(e.target.value)}
                        className="kp-focus border border-neutral-300 px-3 py-2 text-[13px] bg-white rounded-[3px]">
                        {years.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                    <button data-testid={IRC_S1.reloadBtn} onClick={() => load(year)} disabled={loading}
                        className="p-2 border border-neutral-300 rounded-[3px] disabled:opacity-40">
                        <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
                    </button>
                    <button data-testid={IRC_S1.downloadBtn} onClick={download} disabled={!env || loading}
                        className="inline-flex items-center gap-2 px-4 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] text-white rounded-[3px] disabled:opacity-40"
                        style={{ background: "var(--kp-red)" }}>
                        <Download size={12} /> Download PDF
                    </button>
                </div>
            </div>

            {env && (
                <div className="mb-4 flex items-center gap-2">
                    <span data-testid={IRC_S1.travelColumnBadge}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-display font-semibold uppercase tracking-[0.14em] rounded-[2px]"
                        style={{ background: travelConfirmed ? "#E5F3EA" : "#FFF3D6",
                                 color: travelConfirmed ? "var(--kp-success)" : "#B8721A" }}>
                        {travelConfirmed ? <CheckCircle2 size={10} /> : <AlertTriangle size={10} />}
                        Travel → Col {travelCol} {travelConfirmed ? "" : "· awaiting client answer"}
                    </span>
                </div>
            )}

            {env && missing.length > 0 && (
                <div data-testid={IRC_S1.incompleteBanner}
                    className="mb-6 border rounded-[3px] px-4 py-3 flex items-start gap-3 text-[13px]"
                    style={{ borderColor: "var(--kp-red)", background: "rgba(227,6,19,0.06)", color: "var(--kp-red)" }}>
                    <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                    <div><strong>Missing employer fields:</strong> {missing.join(", ")}. The PDF still
                    downloads with an <em>INCOMPLETE — do not lodge</em> watermark. Fix in
                    <a href="/settings/payroll" className="underline mx-1">Settings · Payroll rules</a>.</div>
                </div>
            )}

            {rows.length > 0 && (
                <div className="mb-6 border border-neutral-200 rounded-[3px] bg-white overflow-x-auto">
                    <table className="w-full text-[12px]">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-[0.12em] text-neutral-500 font-display"
                                style={{ background: "#FAFAFA" }}>
                                <th className="px-3 py-2">Employee</th>
                                <th className="px-3 py-2 text-right">1 · Wages</th>
                                <th className="px-3 py-2 text-right">2 · Tax. allow</th>
                                <th className="px-3 py-2 text-right">3 · Tax. benefits</th>
                                <th className="px-3 py-2 text-right">4 · Non-tax</th>
                                <th className="px-3 py-2 text-right">5a · Term. pre-94</th>
                                <th className="px-3 py-2 text-right">5b · Term. post-94</th>
                                <th className="px-3 py-2 text-right">6 · SWT</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.employee_id} data-testid={IRC_S1.row(r.employee_id)} className="border-t border-neutral-100">
                                    <td className="px-3 py-2 text-[color:var(--kp-charcoal)]">
                                        <div>{r.name}</div>
                                        <div data-testid={IRC_S1.rowDeps(r.employee_id)} className="text-[10px] text-neutral-400 font-mono-money">
                                            TIN {r.tin || "pending"} · deps {r.dependants_declared} decl / {r.dependants_for_rebate} rebate · {r.fortnights} fn
                                        </div>
                                    </td>
                                    <td data-testid={IRC_S1.rowCol1(r.employee_id)} className="px-3 py-2 text-right font-mono-money">{r.col_1_gross_wages_kina}</td>
                                    <td data-testid={IRC_S1.rowCol2(r.employee_id)} className="px-3 py-2 text-right font-mono-money">{r.col_2_taxable_allowances_kina}</td>
                                    <td data-testid={IRC_S1.rowCol3(r.employee_id)} className="px-3 py-2 text-right font-mono-money">{r.col_3_taxable_benefits_kina}</td>
                                    <td className="px-3 py-2 text-right font-mono-money text-neutral-400">{r.col_4_non_taxable_allowances_kina}</td>
                                    <td className="px-3 py-2 text-right font-mono-money text-neutral-400">{r.col_5a_termination_pre_1994_kina}</td>
                                    <td className="px-3 py-2 text-right font-mono-money text-neutral-400">{r.col_5b_termination_post_1994_kina}</td>
                                    <td data-testid={IRC_S1.rowCol6(r.employee_id)} className="px-3 py-2 text-right font-mono-money font-semibold" style={{ color: r.col_6_total_swt_toea ? "var(--kp-red)" : "#8B8B90" }}>
                                        {r.col_6_total_swt_kina}
                                    </td>
                                </tr>
                            ))}
                            <tr className="border-t-2 border-neutral-800">
                                <td className="px-3 py-3 font-display font-semibold">Total · {rows.length} employees</td>
                                <td data-testid={IRC_S1.totalWages}      className="px-3 py-3 text-right font-mono-money font-semibold">{totals.wages_kina}</td>
                                <td data-testid={IRC_S1.totalAllowances} className="px-3 py-3 text-right font-mono-money font-semibold">{totals.taxable_allowances_kina}</td>
                                <td data-testid={IRC_S1.totalBenefits}   className="px-3 py-3 text-right font-mono-money font-semibold">{totals.taxable_benefits_kina}</td>
                                <td className="px-3 py-3 text-right text-neutral-400">K0.00</td>
                                <td className="px-3 py-3 text-right text-neutral-400">K0.00</td>
                                <td className="px-3 py-3 text-right text-neutral-400">K0.00</td>
                                <td data-testid={IRC_S1.totalSwt} className="px-3 py-3 text-right font-mono-money font-semibold" style={{ color: "var(--kp-red)" }}>{totals.swt_kina}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}

            {env?.assumptions?.length > 0 && (
                <div className="mb-6 border border-amber-200 rounded-[3px] p-4" style={{ background: "#FFFAEA" }}>
                    <div className="flex items-center gap-2 mb-2 text-[12px] font-display font-semibold uppercase tracking-[0.14em]"
                        style={{ color: "#8A5B0F" }}>
                        <Info size={12} /> Column mapping notes
                    </div>
                    <ul className="space-y-1.5 text-[12px]" style={{ color: "#5D3F0A" }}>
                        {env.assumptions.map((a) => (
                            <li key={a.key} className="flex gap-2">
                                <span style={{ color: a.status === "confirmed_by_spec" ? "var(--kp-success)" : "#B57800" }}>•</span>
                                <span>{a.text}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {env && rows.length === 0 && (
                <div className="p-8 text-center border border-dashed border-neutral-300 rounded-[3px] text-neutral-500 text-[13px]">
                    No approved pay periods in {env.header.year}.
                </div>
            )}
        </div>
    );
}
